import { useEffect } from 'react';

import { MDText } from 'i18n-react';

import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import cypressIds from 'CypressId';

import { applyCypressData } from 'Utilities';
import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { VERIFICATION_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsDocumentVerification';

import { StepList } from 'Storybook/components/local/StepList/StepList';

import LocalTexts from '../DocumentCapture.texts.json';

import docReviewImageSrc from './assets/docReview.svg';

import styles from './ReviewStatusSubview.scss';

const LocalT = new MDText(LocalTexts.reviewStatus);

// eslint-disable-next-line operator-linebreak
const collectionDayArticleLink =
  'https://help.motorway.co.uk/hc/en-gb/articles/4410620430866-What-happens-on-the-day-of-collection';

const onHelpArticleClick = () => {
  DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.WHAT_TO_EXPECT_LINK_CLICKED();
};

type GetStepsProps = { acceptedBidDealerName?: string };

const getSteps = ({ acceptedBidDealerName }: GetStepsProps) => [
  {
    content: (
      <div className={styles.docsReviewStep}>
        <h2 className={styles.title}>{LocalT.translate('inProgress.reviewDocumentsStep.title')}</h2>
        <p className={styles.context}>
          {LocalT.translate('inProgress.reviewDocumentsStep.context', {
            dealerName: acceptedBidDealerName ? `, ${acceptedBidDealerName}` : '',
          })}
        </p>
      </div>
    ),
  },
  {
    content: (
      <div className={styles.docsReviewStep}>
        <h2 className={styles.title}>{LocalT.translate('inProgress.scheduleCollectionStep.title')}</h2>
        <p className={styles.context}>{LocalT.translate('inProgress.scheduleCollectionStep.context')}</p>
        <div className={styles.link}>
          <Hyperlink
            {...applyCypressData(cypressIds.links.collectionDayArticleLinkLabel)}
            href={collectionDayArticleLink}
            label={LocalT.translate('inProgress.scheduleCollectionStep.hyperlinkLabel')}
            onClick={onHelpArticleClick}
            size="small"
            target='_blank'
          />
        </div>
      </div>
    ),
  },
];

type ReviewStatusSubviewProps = {
  acceptedBidDealerName?: string;
  statusText?: React.ReactNode;
}

export const ReviewStatusSubview = ({ acceptedBidDealerName, statusText = null } : ReviewStatusSubviewProps) => {
  const steps = getSteps({ acceptedBidDealerName });

  useEffect(() => {
    VERIFICATION_ANALYTICS_EVENTS.DOCS_IN_REVIEW({});
  }, []);

  return (
    <div className={styles.reviewStatusSubview} {...applyCypressData(cypressIds.sections.reviewStatusSubview)}>
      <div className={styles.heading}>
        <div className={styles.imageContainer}>
          <img alt="docReviewImage" className={styles.docReviewImage} src={docReviewImageSrc} />
        </div>
        <h1 className={styles.headingTitle}>{LocalT.translate('inProgress.newTitle')}</h1>
        <p className={styles.headingSubTitle}>{LocalT.translate('inProgress.newSubtitle')}</p>
      </div>
      <StepList steps={steps} />
      {statusText && <div className={styles.statusText}>{statusText}</div>}
    </div>
  );
};
